import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pager from "@/components/Pager";
import { PRIORITIES, LEAD_REJECTION_REASONS, LEAD_REJECTION_LABEL, fmtDay } from "@/lib/meta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, RotateCcw, Trash2, Search, MapPin } from "lucide-react";
import { toast } from "sonner";

const LIMIT = 50;

export default function RejectedLeadsArchive() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: "", city: "", priority: "", reason: "" });
  const [selected, setSelected] = useState([]);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { status: "rejected", limit: LIMIT, skip };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/leads", { params }).then(({ data }) => { setRows(data.items); setTotal(data.total); }).finally(() => setLoading(false));
  }, [f, skip]);
  useEffect(() => { setSkip(0); setSelected([]); }, [f]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v === "all" ? "" : v }));
  const allOn = rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const bulk = async (action, ids) => {
    try {
      const { data } = await api.post("/leads/bulk", { action, ids });
      toast.success(`${data.affected} lead(s) ${action === "restore" ? "restored to Leads" : "deleted"}`);
      setSelected([]); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-8" data-testid="rejected-leads-archive">
      <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-heading font-bold text-gray-900">Rejected leads</div>
          <div className="text-xs text-gray-500">Leads rejected before becoming a deal. Restore to send them back to Leads.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={f.reason || "all"} onValueChange={set("reason")}>
            <SelectTrigger className="w-40" data-testid="archive-reason-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All reasons</SelectItem>
              {LEAD_REJECTION_REASONS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.priority || "all"} onValueChange={set("priority")}>
            <SelectTrigger className="w-32" data-testid="archive-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All priority</SelectItem>
              {PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative w-40">
            <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input placeholder="City / location" value={f.city} onChange={(e) => set("city")(e.target.value)} className="pl-8" data-testid="archive-city-filter" />
          </div>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input placeholder="Search name, phone…" value={f.search} onChange={(e) => set("search")(e.target.value)} className="pl-8" data-testid="archive-search" />
          </div>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="flex items-center gap-2 bg-[#0047FF]/5 border border-[#0047FF]/20 rounded-lg px-3 py-2 mb-3" data-testid="archive-bulk-bar">
          <span className="text-sm font-semibold">{selected.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => bulk("restore", selected)} data-testid="archive-bulk-restore"><RotateCcw className="h-3.5 w-3.5" /> Restore</Button>
          {user?.role === "admin" && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setConfirmDel(true)} data-testid="archive-bulk-delete"><Trash2 className="h-3.5 w-3.5" /> Delete permanently</Button>}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm" data-testid="archive-empty">No rejected leads.</div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-gray-50">
              <TableHead className="w-10"><Checkbox checked={allOn} onCheckedChange={() => setSelected(allOn ? [] : rows.map((r) => r.id))} data-testid="archive-select-all" /></TableHead>
              <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>City</TableHead>
              <TableHead>Reason</TableHead><TableHead>Rejected</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((l) => (
                <TableRow key={l.id} data-testid={`archive-row-${l.id}`}>
                  <TableCell><Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`archive-select-${l.id}`} /></TableCell>
                  <TableCell className="font-medium">{l.name || "—"}{l.priority && <span className="ml-2 text-[10px] text-gray-500 capitalize">{l.priority}</span>}</TableCell>
                  <TableCell>{l.phone || "—"}</TableCell>
                  <TableCell>{l.city || "—"}</TableCell>
                  <TableCell data-testid={`archive-reason-${l.id}`}>
                    <span className="text-xs font-semibold text-rose-600">{LEAD_REJECTION_LABEL[l.rejection_reason?.code] || l.rejection_reason?.code || "—"}</span>
                    {l.rejection_reason?.note && <div className="text-[11px] text-gray-500 max-w-xs truncate">{l.rejection_reason.note}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">{fmtDay(l.rejected_at || l.updated_at)}{l.rejected_by_name ? ` · ${l.rejected_by_name}` : ""}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => bulk("restore", [l.id])} data-testid={`archive-restore-${l.id}`}><RotateCcw className="h-3.5 w-3.5" /> Restore</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="text-xs text-gray-400" data-testid="archive-total">{total} rejected lead(s)</div>
        <Pager skip={skip} limit={LIMIT} total={total} onPrev={() => setSkip(Math.max(0, skip - LIMIT))} onNext={() => setSkip(skip + LIMIT)} />
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-white" data-testid="archive-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.length} lead(s) permanently?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => bulk("delete", selected)} data-testid="archive-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
